/**
 * FilterCondition - Single filter row component
 */

import { useMemo } from 'react';
import type { FilterCondition as FilterConditionType, ColumnDefinition, FilterOperator, RelativeDate } from '../columns/types';
import {
  OPERATOR_LABELS,
  RELATIVE_DATE_LABELS,
  OPERATORS_BY_TYPE,
  operatorNeedsValue,
} from './constants';
import { useCategories, useTags } from '@/hooks/useTaxonomies';

interface FilterConditionProps {
  filter: FilterConditionType;
  columns: ColumnDefinition[];
  siteId?: string;
  onUpdate: (id: string, updates: Partial<Omit<FilterConditionType, 'id'>>) => void;
  onRemove: (id: string) => void;
}

export function FilterCondition({ filter, columns, siteId, onUpdate, onRemove }: FilterConditionProps) {
  // Fetch taxonomy terms for multiselect columns
  const { data: categories = [] } = useCategories(siteId);
  const { data: tags = [] } = useTags(siteId);

  // Find the column definition for this filter
  const column = useMemo(
    () => columns.find(c => c.key === filter.column),
    [columns, filter.column]
  );

  // Get available operators for this column
  const availableOperators = useMemo(() => {
    if (!column) return [];
    return column.filterOperators || OPERATORS_BY_TYPE[column.type] || [];
  }, [column]);

  // Filterable columns only
  const filterableColumns = useMemo(
    () => columns.filter(c => c.filterable),
    [columns]
  );

  // Get options for multiselect columns (categories or tags)
  const multiselectOptions = useMemo(() => {
    if (!column || column.type !== 'multiselect') return [];
    if (column.key === 'categories') {
      return categories.map(c => ({ value: c.value, label: `${c.label} (${c.count})` }));
    }
    if (column.key === 'tags') {
      return tags.map(t => ({ value: t.value, label: `${t.label} (${t.count})` }));
    }
    return [];
  }, [column, categories, tags]);

  // Handle column change
  const handleColumnChange = (newColumnKey: string) => {
    const newColumn = columns.find(c => c.key === newColumnKey);
    if (!newColumn) return;

    const newOperators = newColumn.filterOperators || OPERATORS_BY_TYPE[newColumn.type] || [];
    const newOperator = newOperators[0] || 'eq';

    onUpdate(filter.id, {
      column: newColumnKey,
      operator: newOperator,
      value: '',
      relativeTo: undefined,
    });
  };

  // Handle operator change
  const handleOperatorChange = (newOperator: FilterOperator) => {
    onUpdate(filter.id, {
      operator: newOperator,
      // Reset value if switching to exists/not_exists
      value: operatorNeedsValue(newOperator) ? filter.value : undefined,
      relativeTo: undefined,
    });
  };

  // Handle value change
  const handleValueChange = (newValue: unknown) => {
    onUpdate(filter.id, { value: newValue, relativeTo: undefined });
  };

  // Handle relative date change
  const handleRelativeDateChange = (relativeTo: RelativeDate) => {
    onUpdate(filter.id, { relativeTo, value: undefined });
  };

  if (!column) {
    return null;
  }

  const needsValue = operatorNeedsValue(filter.operator);
  const isDateColumn = column.type === 'date';

  return (
    <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg border border-zinc-700">
      {/* Column selector */}
      <select
        value={filter.column}
        onChange={(e) => handleColumnChange(e.target.value)}
        className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500"
      >
        {filterableColumns.map(col => (
          <option key={col.key} value={col.key}>
            {col.label}
          </option>
        ))}
      </select>

      {/* Operator selector */}
      <select
        value={filter.operator}
        onChange={(e) => handleOperatorChange(e.target.value as FilterOperator)}
        className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500"
      >
        {availableOperators.map(op => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {/* Value input - varies by column type */}
      {needsValue && (
        <>
          {isDateColumn ? (
            // Date columns: show relative date dropdown or date picker
            <div className="flex gap-1">
              <select
                value={filter.relativeTo || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    handleRelativeDateChange(e.target.value as RelativeDate);
                  } else {
                    onUpdate(filter.id, { relativeTo: undefined });
                  }
                }}
                className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Pick date...</option>
                <optgroup label="Relative">
                  {Object.entries(RELATIVE_DATE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </optgroup>
              </select>
              {!filter.relativeTo && (
                <input
                  type="date"
                  value={String(filter.value || '')}
                  onChange={(e) => handleValueChange(e.target.value)}
                  className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500"
                />
              )}
            </div>
          ) : column.type === 'select' && column.options ? (
            // Select columns: dropdown
            <select
              value={String(filter.value || '')}
              onChange={(e) => handleValueChange(e.target.value)}
              className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">Select...</option>
              {column.options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : column.type === 'boolean' ? (
            // Boolean columns: true/false dropdown
            <select
              value={String(filter.value)}
              onChange={(e) => handleValueChange(e.target.value === 'true')}
              className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500"
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : column.type === 'number' || column.type === 'currency' ? (
            // Number/currency: number input
            <input
              type="number"
              value={String(filter.value || '')}
              onChange={(e) => handleValueChange(e.target.value ? Number(e.target.value) : '')}
              placeholder="Value..."
              className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500 w-24"
            />
          ) : column.type === 'multiselect' && multiselectOptions.length > 0 ? (
            // Multiselect (categories/tags): dropdown with options
            <select
              value={String(filter.value || '')}
              onChange={(e) => handleValueChange(e.target.value)}
              className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500 min-w-[150px]"
            >
              <option value="">Select {column.label}...</option>
              {multiselectOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            // Text: text input
            <input
              type="text"
              value={String(filter.value || '')}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Value..."
              className="bg-zinc-700 text-zinc-100 text-sm rounded px-2 py-1.5 border-0 focus:ring-1 focus:ring-blue-500 min-w-[120px]"
            />
          )}
        </>
      )}

      {/* Remove button */}
      <button
        onClick={() => onRemove(filter.id)}
        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded transition-colors"
        title="Remove filter"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
